import {Download} from '@mui/icons-material'
import {IconButton, MenuItem, Menu, Tooltip} from '@mui/material'
import {useRef, useState, useMemo} from 'react'
import {CSVLink} from 'react-csv'
import {saveAs} from 'file-saver'
import {useExport} from '../../store/hooks/export'
import {useConfig} from '../../store/hooks/config'
import {pdf} from '@react-pdf/renderer'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import PDFTemplate from '../Dashboard/charts/PopulationDensityChart/PDFLayout/PDFTemplate'
import {AnalyticsHandler} from '../../shared/handlers/analytics/handlers'
import {getUpdatedData} from '../Dashboard/charts/PopulationDensityChart/helpers'

export const getMapImage = async (map) => {
  return new Promise((resolve, reject) => {
    if (map) {
      map.setPixelRatio(300 / 48)
      map.once('idle', () => {
        const image = map.getCanvas().toDataURL('image/png')
        if (image) {
          resolve(image)
        } else {
          reject(new Error('Failed to generate map image'))
        }
      })
    } else {
      reject(new Error('Map reference is not available'))
    }
  })
}
const ExportButton = ({disabled = true, pdfData}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const {exportTableData, isMapDataLoading} = useExport()
  const {personasConfig} = useConfig()
  const csvLink = useRef()
  const {mapRef} = pdfData

  // Memoize CSV data to prevent unnecessary recalculations
  const memoizedCsvData = useMemo(
    () => (exportTableData.tableRows === undefined ? [] : getUpdatedData(exportTableData.tableRows, personasConfig)),
    [exportTableData.tableRows, personasConfig]
  )

  const generateFilename = (type) => {
    return dayjs().format(`[${type}_]MM_DD_YYYY_hh_mm_ssA`)
  }

  const downLoadPDF = async () => {
    if (!mapRef) {
      console.error('Map is not ready yet for export')
      return
    }
    const timeStamp = dayjs().format('MM/DD/YYYY [|] hh:mm:ss A')
    await getMapImage(mapRef)
    const pdfD = <PDFTemplate {...pdfData} timeStamp={timeStamp} />
    const filename = generateFilename('map')
    const asPdf = pdf(pdfD)
    const blob = await asPdf.toBlob()
    saveAs(blob, `${filename}.pdf`)
  }
  return (
    <div>
      <CSVLink
        ref={csvLink}
        style={{display: 'none'}}
        data={memoizedCsvData}
        headers={exportTableData.tableColNames}
        filename={`${generateFilename('populationcount')}.csv`}
      />
      <Tooltip title='Export' placement='bottom'>
        <IconButton
          disabled={disabled || isMapDataLoading}
          onClick={(e) => {
            setAnchorEl(e.currentTarget)
          }}
          id='export-button'
          aria-label='export'
          aria-controls={open ? 'basic-menu' : undefined}
          size='medium'
          data-testid='export-button'
        >
          <Download variant='primaryIcon' sx={{fontSize: (theme) => theme.typography.kpiMedium.fontSize}} />
        </IconButton>
      </Tooltip>
      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        onClose={() => {
          setAnchorEl(null)
        }}
        MenuListProps={{
          'aria-labelledby': 'export-button'
        }}
      >
        <MenuItem
          onClick={async () => {
            setAnchorEl(null)
            AnalyticsHandler.onExport({
              type: 'Single',
              fileType: 'PDF',
              source: 'Dashboard'
            })
            downLoadPDF()
          }}
          data-testid='pdf-export'
        >
          PDF
        </MenuItem>

        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            AnalyticsHandler.onExport({
              type: 'Single',
              fileType: 'CSV',
              source: 'Dashboard'
            })
            csvLink.current.link.click()
          }}
          data-testid='csv-export'
        >
          CSV
        </MenuItem>

        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            csvLink.current.link.click()
            AnalyticsHandler.onExport({
              type: 'Both',
              fileType: 'CSV',
              source: 'Dashboard'
            })
            downLoadPDF()
            setTimeout(() => {
              AnalyticsHandler.onExport({
                type: 'Both',
                fileType: 'PDF',
                source: 'Dashboard'
              })
            }, 400)
          }}
          data-testid='both-export'
        >
          Both
        </MenuItem>
      </Menu>
    </div>
  )
}
ExportButton.propTypes = {
  disabled: PropTypes.bool,
  pdfData: PropTypes.object
}
export default ExportButton

/* Function Descriptions:
  - generateFilename: Creates a timestamped filename based on the provided type.
  - downLoadPDF: Generates and downloads a PDF file using the provided data and template.
  - ExportButton: Renders an export button that allows users to download data in CSV or PDF format.
  - AnalyticsHandler: Tracks user interactions with the export button for analytics purposes.
*/
